// Manager home (`/manager`) — the PO's four action cards, at EVERY width
// (PO 2026-08-06).
//
// WHAT CHANGED. This page used to be two pages in one tree: the Maeve-First
// cards below `md`, and a greeting hero + searchable/filterable/paged pathway
// list at `md`+, each `enabled`-gated so only one query fired. The PO's note
// is about what a manager meets when the app loads, without qualifying width,
// and the four cards are that on a laptop as much as on a phone. So the cards
// are now the whole page and the pathway list moved wholesale to
// `/manager/team` — the tab that was already the pathway list, and the
// destination of the fourth card.
//
// The `useMediaQuery`/`enabled` split is therefore gone: one query, one
// rendered tree, no viewport branch left on this page at all. (`ManagerTeam`
// picked up the search/filter/pager, so nothing was lost in the move — see
// its header.)

import { useSession } from '@/lib/auth.ts';
import { useCases } from '@/lib/use-cases.ts';
import { firstNameFromSession } from '@/lib/format.ts';
import { ManagerMaeveFirst } from '@/components/ManagerMaeveFirst.tsx';

export function ManagerDashboard() {
  const session = useSession('manager-shell');
  // Unconditional now. The cards' DESTINATIONS no longer depend on this — see
  // `ManagerMaeveFirst` — but the pathway card's counts and the
  // return-to-work continue link still read it, and a manager landing here
  // wants those counts at every width.
  const cases = useCases();
  const firstName = firstNameFromSession(session as { name?: string });

  if (cases.isPending) return <p className="text-muted-foreground">Loading support pathways…</p>;

  return (
    <ManagerMaeveFirst
      // A failed list is NOT a failed page any more. Three of the four cards
      // don't read `cases` at all, so an API blip costs a count, not the
      // manager's entire entry point — the old desktop branch could afford to
      // render an error instead of the page because the list WAS the page.
      // `casesUnavailable` is what keeps that degradation honest rather than
      // silently reporting an empty caseload; see its doc comment.
      cases={cases.data?.cases ?? []}
      casesUnavailable={cases.error != null}
      firstName={firstName}
    />
  );
}
